import { useEffect, useId, useRef, useState } from 'react'
import { InfoCircleOutlined } from '../../components/icons'
import { Chip } from '../../components/ui/Chip'
import { Tooltip } from '../../components/ui/Tooltip'
import { formatMoney } from '../cart/cart-money'
import type { QuoteVariant } from './domain'
import { qualitativeVariantLabels } from './quote-variant-labels'
import { REGION_GROUPS } from './region-groups'

/**
 * UID-434 region-grouping ADR note (2026-09-02 design spec, no ticket) — the
 * "Choose a quote option" list regroups into two visually-labeled columns,
 * North America / Overseas, matching the existing domestic/overseas split
 * `variant-pricing.ts`'s `VARIANT_TO_DELIVERY_OPTION` and the server's
 * `rfq-cost-estimate.ts` already use. This is PRESENTATION-ONLY: group
 * membership is derived here, at render time, by filtering the `variants`
 * prop by `id` — it never reorders/mutates `variants`, never reaches into
 * `RFQ_QUOTE_VARIANTS` directly, and never changes an `id` (frozen contract
 * enum echoed back at cart-commit, ADR-0051). The 2/2 column split is the
 * real, PERMANENT shape of this component (4 total variants: North America =
 * us + prefer_us_blend, Overseas = offshore + offshore_economy), not a
 * loading/empty edge case — a group that ends up with a single matched
 * variant still renders its group header for layout consistency and because
 * the header id is also the column's accessible-group label (see the
 * `role="group"`/`aria-labelledby` wiring below).
 *
 * UID-440 (PHASE 0) — `REGION_GROUPS`/`groupTitleForVariantId` are now
 * extracted to `./region-groups` (single source of truth for the region-group
 * title/sublabel/tooltip vocabulary, so other RFQ surfaces — e.g.
 * `RfqDetailSummaryRail`'s Region row — can't drift from this component's
 * copy). Must be kept in sync there with any future addition to
 * `QuoteVariant['id']`; an id that matches none of these groups falls into a
 * rendered "Other options" fallback (below) rather than silently
 * disappearing.
 */

// UID-137 §2.7 (ADR-0060 §3): mirrors tokens.css's `--duration` (200ms) as a
// literal — a JS timer can't read a CSS custom property at runtime, so the
// two are kept in lockstep by comment/convention, same as
// OrderPlacedCelebration.tsx's `FULL_SEQUENCE_MS`/`GENTLE_SEQUENCE_MS`.
const PRICE_FLASH_CALC_MS = 200 // --duration
const PRICE_FLASH_SETTLE_MS = 200 // --duration

interface PriceFlash {
  readonly variantId: QuoteVariant['id']
  readonly phase: 'calc' | 'settle'
}

export interface QuoteVariantSelectorProps {
  /** The Quote's `variants`, rendered in the exact order given (order is
   * significant — us -> offshore -> offshore_economy -> prefer_us_blend,
   * ADR-0051). */
  readonly variants: readonly QuoteVariant[]
  readonly selectedVariantId: QuoteVariant['id']
  readonly onSelect: (id: QuoteVariant['id']) => void
  /**
   * Pre-computed preview total per variant id (decimal STRING, ADR-0004),
   * keyed by `QuoteVariant['id']`. QA (UID-116 defect) fix: this component
   * NEVER derives a preview by scaling an aggregate base amount itself —
   * scaling the sum-of-lines once and rounding is NOT the same number as
   * rounding each line's scaled amount individually and then summing (a
   * classic rounding-order divergence), and the real `/cart/lines` POSTs
   * round per line. The caller (`QuotedLinesPanel`, RfqCard.tsx) owns the
   * base-price context and MUST compute each entry here the exact same
   * per-line-rounded way it builds the actual cart POST, so what this
   * selector shows always equals what lands in the cart.
   */
  readonly previewAmounts: Readonly<Record<QuoteVariant['id'], string>>
  readonly currency: string
}

/**
 * UID-116 (ADR-0051) — the per-quote pricing-region variant picker.
 * Originally (components.md "Radio / radio-dot": "Purple-dot variant for
 * RFQ region select") painted with the shared `.radio-dot` purple
 * accent-color input and the `--color-accent-purple-bg`/`-border` tokens.
 *
 * UID-294 (ADR-0104 D1, 2026-08-25) — REVERSES that purple selected-state
 * for THIS surface only, per the design source of truth
 * (`Fictiv Platform.dc.html:9807-9808`): the selected card is now
 * `background:#EEFFF9` (mint, RFQ-scoped only — see `.quote-variant-groups`
 * in rfq.css) + `1.5px solid var(--color-brand-primary)` (navy — the design's
 * `var(--fictiv-teal)` resolves to this app's already-existing navy brand
 * token, NOT a new teal hue), and the radio indicator is an 18x18 navy-filled
 * circle with a white checkmark rather than a plain dot. This does NOT
 * repaint the shared `.radio-dot` class (still purple everywhere else —
 * `AddOnServicesCard.tsx`/`TargetingCard.tsx` via `Radio.tsx`'s
 * `RadioGroup`); a new RFQ-scoped class (`.quote-variant-radio-dot`, rfq.css)
 * carries the navy/checkmark paint via the SAME real-`<input>`-under-
 * custom-CSS-paint technique `ui.css`'s `.checkbox-input` already
 * establishes (`appearance:none` + a `:checked::after` border-drawn
 * checkmark) — still a real native `<input type="radio">` group (free
 * keyboard arrow-nav + single-checked invariant, same approach as
 * `Radio.tsx`'s `RadioGroup`), so keyboard operation and `:focus-visible`
 * are unaffected by the repaint. ADR-0051/0060/0064-D4's "purple is the
 * selection color; do not introduce teal" ruling continues to govern every
 * OTHER selection surface in the app — this reversal is surface-scoped to
 * the RFQ quote-option cards only.
 *
 * Purely presentational and controlled — `QuotedLinesPanel` (RfqCard.tsx)
 * owns the selection state and pre-computes each variant's preview total
 * (`previewAmounts`) using the same per-line rounding as the real cart POST.
 *
 * The framing block ("All parts priced" / "Choose a quote option" / the
 * subcopy) is hardcoded, NOT injectable. UID-260 gap #2 briefly made it
 * injectable so `RfqPreQuotePricingPreview.tsx` could reuse this component
 * pre-quote with honest preview-only copy instead of this real post-quote
 * framing. ADR-0125 D3 (UID-401, Gate-5 W7) REMOVED that caller's overrides —
 * `RfqPreQuotePricingPreview` now renders this post-quote framing verbatim,
 * which is a decision for that component's own doc comment, not this one —
 * so as of this ticket NO caller passes anything but the defaults. Deleted
 * (not left as unused optional props) rather than kept "for later": a
 * grep confirmed there is no other consumer, and unused injectability is
 * exactly the kind of drift this ticket exists to catch.
 */
export function QuoteVariantSelector({
  variants,
  selectedVariantId,
  onSelect,
  previewAmounts,
  currency,
}: QuoteVariantSelectorProps) {
  const baseId = useId()
  const groupLabel = 'Pricing region'
  const isFirstRender = useRef(true)
  const [priceFlash, setPriceFlash] = useState<PriceFlash | undefined>(undefined)

  // UID-137 §2.7 (ADR-0060 §3): a brief calc->settle flash on the NEWLY
  // selected variant's price only (UX spec §2.7 — the other options' preview
  // prices update silently). Purely presentational: never touches
  // `previewAmounts` or how/when it's computed (ADR-0051/UID-126 boundary) —
  // just a CSS-class sequence layered on top of the already-correct,
  // already-rendered text this component receives as a prop.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // UX spec §4: reduced motion skips the flash entirely and shows the
    // settled amount immediately — never a slowed-down shimmer.
    if (prefersReducedMotion) return
    setPriceFlash({ variantId: selectedVariantId, phase: 'calc' })
    const toSettle = window.setTimeout(
      () => setPriceFlash({ variantId: selectedVariantId, phase: 'settle' }),
      PRICE_FLASH_CALC_MS,
    )
    const toIdle = window.setTimeout(
      () => setPriceFlash(undefined),
      PRICE_FLASH_CALC_MS + PRICE_FLASH_SETTLE_MS,
    )
    return () => {
      window.clearTimeout(toSettle)
      window.clearTimeout(toIdle)
    }
  }, [selectedVariantId])

  // UID-434 region-grouping — build each group's rendered variant list by
  // FILTERING the `variants` prop (never sorting/mutating it, never reading
  // `RFQ_QUOTE_VARIANTS` directly), then drop any group that ends up with no
  // matched variants (edge case: a truncated/anomalous `variants` array).
  // Any variant whose `id` matches none of `REGION_GROUPS` (edge case: a
  // future new variant id landing before this map is updated) falls into a
  // synthesized "Other options" fallback group instead of silently vanishing
  // from the UI.
  // UID-622 (cart follow-up) — ranked ACROSS ALL variants passed in (never per region
  // group, which would award "Fastest" independently within each column).
  const labelsByVariantId = qualitativeVariantLabels(variants)
  const groupedIds = new Set(REGION_GROUPS.flatMap((group) => group.variantIds))
  const otherVariants = variants.filter((variant) => !groupedIds.has(variant.id))
  const renderGroups = [
    ...REGION_GROUPS.map((group) => ({
      ...group,
      items: variants.filter((variant) => group.variantIds.includes(variant.id)),
    })).filter((group) => group.items.length > 0),
    ...(otherVariants.length > 0
      ? [
          {
            key: 'other',
            title: 'Other options',
            sublabel: undefined,
            tooltip: undefined,
            items: otherVariants,
          },
        ]
      : []),
  ]

  return (
    <div className="quote-variant-selector">
      {/* UID-198 (UX spec §3.1) — the "Choose a quote option" framing block.
          The old visible `<p className="form-field-label">{groupLabel}</p>`
          is removed; its accessible-name job is already carried by the
          radiogroup's own `aria-label` below, unchanged (WCAG 4.1.2).
          ADR-0115 F2 (dc.html:3522-3523) — the chip+heading are their own
          flex ROW; the subcopy is a SEPARATE sibling paragraph below it,
          not a third child stacked inside the same row. */}
      <div className="quote-variant-framing">
        <h2 className="quote-variant-heading">Choose a quote option</h2>
        <Chip tone="success" className="quote-variant-priced-chip">
          All parts priced
        </Chip>
      </div>
      <p className="quote-variant-subcopy">
        Pick a sourcing region and check out from the summary on the right.
      </p>
      {/* UID-434 — ONE radiogroup still wraps every radio across BOTH visual
       * columns (single-select stays correct: one shared `name`, native
       * roving-tabindex arrow-nav crosses the column boundary same as any
       * other radio group). Each `.quote-variant-group` below is a nested
       * `role="group"` purely for supplementary AT/structural grouping — do
       * NOT split this into two independent radiogroups, which would allow
       * one radio per group to be checked simultaneously and break the
       * single-select contract this component has always had. */}
      <div
        role="radiogroup"
        aria-label={groupLabel}
        className={[
          'quote-variant-groups',
          renderGroups.length === 1 && 'quote-variant-groups--single',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {renderGroups.map((group) => {
          const groupTitleId = `${baseId}-group-${group.key}-title`
          return (
            <div
              key={group.key}
              className="quote-variant-group"
              // A supplementary AT grouping landmark nested inside the outer
              // radiogroup, not a form — <fieldset> would bring unwanted
              // default browser border/padding into this CSS grid column
              // (same reasoning/precedent as RfqCard.tsx's bulk-action bar
              // and Avatar.tsx's `AvatarStack`).
              // biome-ignore lint/a11y/useSemanticElements: see comment above.
              role="group"
              aria-labelledby={groupTitleId}
            >
              <div className="quote-variant-group-header">
                <h3 id={groupTitleId} className="quote-variant-group-title">
                  {group.title}
                </h3>
                {group.tooltip !== undefined && (
                  <Tooltip label={group.tooltip}>
                    <button
                      type="button"
                      className="quote-variant-group-info"
                      aria-label={`About ${group.title} pricing`}
                    >
                      <InfoCircleOutlined aria-hidden="true" />
                    </button>
                  </Tooltip>
                )}
              </div>
              <div className="quote-variant-group-options">
                {group.items.map((variant) => {
                  const checked = variant.id === selectedVariantId
                  const inputId = `${baseId}-${variant.id}`
                  const previewAmount = previewAmounts[variant.id] ?? '0.00'
                  const classes = [
                    'quote-variant-option',
                    checked && 'quote-variant-option-checked',
                  ]
                    .filter(Boolean)
                    .join(' ')
                  const priceClasses = [
                    'quote-variant-price',
                    'fx-price',
                    priceFlash?.variantId === variant.id && `fx-price-${priceFlash.phase}`,
                  ]
                    .filter(Boolean)
                    .join(' ')
                  return (
                    <label key={variant.id} htmlFor={inputId} className={classes}>
                      <input
                        id={inputId}
                        type="radio"
                        name={`${baseId}-quote-variant`}
                        value={variant.id}
                        checked={checked}
                        aria-checked={checked}
                        aria-describedby={groupTitleId}
                        onChange={() => onSelect(variant.id)}
                        className="quote-variant-radio-dot"
                      />
                      {/* UID-434 — the row is now TWO text elements on the
                       * left (bold lead time + region sub-label) and the
                       * price on the right, replacing the old THREE-stacked-
                       * row card (region / price / lead, UID-325).
                       *
                       * UID-622 (cart follow-up, ADR-0181 D3 OPEN-note
                       * addendum) — the "Fastest"/"Cost-effective" qualifier
                       * from the sample screenshot, SUPPRESSED since
                       * UID-434/UID-440, is now RE-ENABLED: ADR-0181 D3
                       * repriced `prefer_us_blend` below `us`, making the
                       * ladder monotone (longer lead time = cheaper) in North
                       * America as well as Overseas, and this ticket is the
                       * direct product decision to turn the copy back on —
                       * resolving that ADR's "OPEN, routed to PM" note. The
                       * label is rendered below the price (see
                       * `quote-variant-price-label`), ranked via the SAME
                       * shared `qualitativeVariantLabels` utility the cart
                       * page's `CartQuoteOptionSelector` uses, so the two
                       * surfaces can never disagree on which option is
                       * "Fastest"/"Standard"/"Cost-effective". `variant.note`
                       * remains unrendered, same as before (UID-325). */}
                      <span className="quote-variant-body">
                        <span className="quote-variant-lead">{variant.leadTimeLabel}</span>
                        {/* UID-434 (follow-up) — the sub-label is a FIXED
                         * per-GROUP constant (`group.sublabel`), not derived
                         * from `variant.region`: the reference screenshot
                         * shows one sub-label per COLUMN ("USA or Mexico" /
                         * "Overseas production"), not one per option, so
                         * both North America rows (`us`, `prefer_us_blend`)
                         * render the identical sub-label despite having
                         * different `region` values. `variant.region` is no
                         * longer read anywhere in this component — the
                         * server-authored region string still exists on the
                         * wire (UID-289), it just isn't displayed here.
                         * Omitted entirely for the "Other options" fallback
                         * group, which has no reference-design copy. */}
                        {group.sublabel !== undefined && (
                          <span className="quote-variant-sublabel">{group.sublabel}</span>
                        )}
                      </span>
                      <span className={priceClasses}>
                        {/* UID-607 — `previewAmount` is the option's real
                         * summed TOTAL (`quotePreview` in `RfqCard.tsx`),
                         * not a per-unit rate, so no "/ea" suffix. */}
                        {formatMoney(previewAmount, currency)}
                        <span className="quote-variant-price-label">
                          {labelsByVariantId.get(variant.id)}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
